/**
 * Focus Shell Store
 *
 * Store de Zustand para gestionar el estado del shell.
 * Maneja tabs, usuario, apps disponibles y UI state.
 *
 * Features:
 * - Session persistence via SessionManager
 * - BroadcastChannel sync between windows
 * - Pop-out window support
 *
 * @see F:\SummaFX Code\Focus\src\types\focus-shell.ts
 */

import { create } from 'zustand'
import { v4 as uuidv4 } from 'uuid'
import type {
  FocusShellStore,
  FocusUser,
  AppMetadata,
  AppTab,
  ClientOrgParams,
  DEFAULT_APPS
} from '@/types/focus-shell'
import { sessionManager, type SavedTab } from '@/services/session/SessionManager'
import { broadcastSync } from '@/services/session/BroadcastSync'

const initialState = {
  // Auth
  isAuthenticated: false,
  isLoading: true,
  user: null,

  // Entitlements
  availableApps: [] as AppMetadata[],
  enableUat: false,
  orgParams: {} as ClientOrgParams,

  // Tabs
  openTabs: [] as AppTab[],
  activeTabId: null,

  // Session
  sessionId: '',
  sessionStartedAt: null,

  // UI
  isSidebarCollapsed: false,
  commandPaletteOpen: false,

  // Connection
  connectionStatus: 'connected' as 'connected' | 'degraded' | 'disconnected',
  connectionLatency: null as number | null,
  shellError: null as string | null,

  // Pop-out windows
  popouts: new Map<string, Window>() as Map<string, Window>,

  // Pop-out tabs (guardados cuando se hace pop-out para poder restaurarlos)
  poppedOutTabs: new Map<string, AppTab>() as Map<string, AppTab>,
}

export const useShellStore = create<FocusShellStore>((set, get) => ({
  ...initialState,

  // Auth actions
  setUser: (user: FocusUser | null) => set({ user }),

  setAuthenticated: (isAuthenticated: boolean) => set({ isAuthenticated }),

  setLoading: (isLoading: boolean) => set({ isLoading }),

  logout: () => {
    // Cerrar todos los popouts antes de logout
    const state = get()
    if (state.poppedOutTabs.size > 0) {
      import('@/services/popout/PopoutManager').then(({ popoutManager }) => {
        popoutManager.closeAllPopouts()
      })
    }
    set(initialState)
  },

  // App/Entitlements actions
  setAvailableApps: (apps: AppMetadata[]) => set({ availableApps: apps }),
  setEnableUat: (enabled: boolean) => set({ enableUat: enabled }),
  setOrgParams: (params) => set({ orgParams: params }),

  // Tab actions
  openApp: (appId: string) => {
    const state = get()
    const app = state.availableApps.find(a => a.appId === appId)

    if (!app) {
      console.warn(`App ${appId} not found in available apps`)
      return
    }

    // Create new tab (allows multiple instances of the same app)
    const newTab: AppTab = {
      tabId: uuidv4(),
      appId,
      instanceId: uuidv4(),
      title: app.name,
      isActive: true,
    }

    const newTabs = [...state.openTabs, newTab]
    set({
      openTabs: newTabs,
      activeTabId: newTab.tabId,
    })

    // Persist session
    sessionManager.scheduleSave(
      newTabs.map(tabToSavedTab),
      newTab.tabId
    )

    // Notify other windows
    broadcastSync.postMessage({
      type: 'TAB_OPENED',
      appId,
    })
  },

  closeTab: (tabId: string) => {
    const state = get()
    const tabIndex = state.openTabs.findIndex(t => t.tabId === tabId)

    if (tabIndex === -1) return

    const newTabs = state.openTabs.filter(t => t.tabId !== tabId)

    // If closing active tab, activate the previous one
    let newActiveId = state.activeTabId
    if (state.activeTabId === tabId) {
      if (newTabs.length > 0) {
        // Activate tab to the left, or first tab if we closed the first one
        const newIndex = Math.max(0, tabIndex - 1)
        newActiveId = newTabs[newIndex]?.tabId || null
      } else {
        newActiveId = null
      }
    }

    set({
      openTabs: newTabs,
      activeTabId: newActiveId,
    })

    // Persist session
    sessionManager.scheduleSave(
      newTabs.map(tabToSavedTab),
      newActiveId
    )

    // Notify other windows
    broadcastSync.postMessage({
      type: 'TAB_CLOSED',
      tabId,
    })
  },

  setActiveTab: (tabId: string) => {
    const state = get()
    set({ activeTabId: tabId })

    // Persist session
    sessionManager.scheduleSave(
      state.openTabs.map(tabToSavedTab),
      tabId
    )
  },

  pinTab: (tabId: string) => {
    const state = get()
    const newTabs = state.openTabs.map(tab =>
      tab.tabId === tabId ? { ...tab, isPinned: true } : tab
    )
    set({ openTabs: newTabs })

    // Persist session
    sessionManager.scheduleSave(
      newTabs.map(tabToSavedTab),
      state.activeTabId
    )
  },

  unpinTab: (tabId: string) => {
    const state = get()
    const newTabs = state.openTabs.map(tab =>
      tab.tabId === tabId ? { ...tab, isPinned: false } : tab
    )
    set({ openTabs: newTabs })

    // Persist session
    sessionManager.scheduleSave(
      newTabs.map(tabToSavedTab),
      state.activeTabId
    )
  },

  updateTabTitle: (tabId: string, title: string) => {
    const state = get()
    const newTabs = state.openTabs.map(tab =>
      tab.tabId === tabId ? { ...tab, title } : tab
    )
    set({ openTabs: newTabs })

    // Persist session
    sessionManager.scheduleSave(
      newTabs.map(tabToSavedTab),
      state.activeTabId
    )
  },

  // Connection actions
  setConnectionStatus: (status: 'connected' | 'degraded' | 'disconnected') => set({ connectionStatus: status }),
  setConnectionLatency: (latency: number | null) => set({ connectionLatency: latency }),
  setShellError: (error: string | null) => set({ shellError: error }),

  // UI actions
  toggleSidebar: () => set(state => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),

  toggleCommandPalette: () => set(state => ({ commandPaletteOpen: !state.commandPaletteOpen })),

  // Session actions
  initSession: () => {
    set({
      sessionId: uuidv4(),
      sessionStartedAt: new Date(),
    })
  },

  // Session persistence
  // userId is passed directly since shell store user might not be set
  loadSession: (userId: string) => {
    if (!userId) {
      console.warn('[ShellStore] Cannot load session: no userId provided')
      return
    }

    // Initialize session manager with provided userId
    sessionManager.initialize(userId)

    // Get fresh state (availableApps should be set by now)
    const state = get()

    // Load saved session
    const savedSession = sessionManager.load()

    console.log('[ShellStore] loadSession called', {
      userId,
      availableApps: state.availableApps.length,
      savedTabs: savedSession?.tabs.length ?? 0
    })

    if (savedSession && savedSession.tabs.length > 0) {
      // Validate tabs against available apps
      const validTabs = savedSession.tabs
        .filter(savedTab => {
          const app = state.availableApps.find(a => a.appId === savedTab.appId)
          if (!app) {
            console.warn(`[ShellStore] Skipping saved tab: app ${savedTab.appId} not available`)
            return false
          }
          return true
        })
        .map(savedTab => savedTabToTab(savedTab))

      if (validTabs.length > 0) {
        // Restore tabs
        set({
          openTabs: validTabs,
          activeTabId: savedSession.activeTabId && validTabs.find(t => t.tabId === savedSession.activeTabId)
            ? savedSession.activeTabId
            : validTabs[0].tabId,
        })
        console.log('[ShellStore] Session restored', { tabs: validTabs.length })
        return
      }
    }

    // No valid saved session - open first available app
    if (state.availableApps.length > 0) {
      const firstApp = state.availableApps[0]
      console.log(`[ShellStore] No saved session, opening default app: ${firstApp.appId}`)
      get().openApp(firstApp.appId)
    }
  },

  // Pop-out actions
  openPopout: (tabId: string) => {
    console.log(`[ShellStore] openPopout called for tabId: ${tabId}`)

    const state = get()
    const tab = state.openTabs.find(t => t.tabId === tabId)

    if (!tab) {
      console.warn(`[ShellStore] Tab ${tabId} not found`)
      return
    }

    console.log('[ShellStore] Found tab to pop out:', tab)

    // Import PopoutManager dynamically (client-side only)
    import('@/services/popout/PopoutManager').then(({ popoutManager }) => {
      const windowId = popoutManager.openPopout({
        appId: tab.appId,
        instanceId: tab.instanceId,
        title: tab.title,
        width: 1200,
        height: 800
      })

      if (windowId) {
        // Guardar tab antes de cerrarlo (para poder restaurarlo cuando se cierre el popout)
        const tabCopy = { ...tab, isPopout: true }
        const newPoppedOutTabs = new Map(state.poppedOutTabs)
        newPoppedOutTabs.set(tab.instanceId, tabCopy)

        console.log(`[ShellStore] Saving tab to poppedOutTabs with instanceId: ${tab.instanceId}`)
        console.log('[ShellStore] Tab copy:', tabCopy)

        set({ poppedOutTabs: newPoppedOutTabs })

        // Close tab from main shell (ahora está en pop-out)
        get().closeTab(tabId)
        console.log(`[ShellStore] Opened ${tab.appId} in pop-out window (instanceId: ${tab.instanceId}, windowId: ${windowId})`)
      } else {
        console.error('[ShellStore] Failed to open pop-out (popup blocker?)')
      }
    })
  },

  // Restaurar tab desde pop-out window
  restoreFromPopout: (instanceId: string) => {
    console.log(`[ShellStore] restoreFromPopout called with instanceId: ${instanceId}`)

    const state = get()
    console.log('[ShellStore] Current poppedOutTabs:', Array.from(state.poppedOutTabs.keys()))

    const savedTab = state.poppedOutTabs.get(instanceId)

    if (!savedTab) {
      console.warn(`[ShellStore] No saved tab found for instanceId ${instanceId}`)
      console.warn('[ShellStore] Available popped out tabs:', Array.from(state.poppedOutTabs.entries()))
      return
    }

    console.log('[ShellStore] Found saved tab:', savedTab)

    // Crear nuevo tab con nuevo ID pero misma app e instanceId
    const restoredTab: AppTab = {
      ...savedTab,
      tabId: uuidv4(), // Nuevo tab ID
      isActive: true,
      isPopout: false, // Ya no está en popout
    }

    const newTabs = [...state.openTabs, restoredTab]
    const newPoppedOutTabs = new Map(state.poppedOutTabs)
    newPoppedOutTabs.delete(instanceId)

    set({
      openTabs: newTabs,
      activeTabId: restoredTab.tabId,
      poppedOutTabs: newPoppedOutTabs,
    })

    // Persist session
    sessionManager.scheduleSave(
      newTabs.map(tabToSavedTab),
      restoredTab.tabId
    )

    console.log(`[ShellStore] Successfully restored tab ${savedTab.appId} from pop-out (${instanceId})`)
  },
}))

// Helper functions
function tabToSavedTab(tab: AppTab): SavedTab {
  return {
    tabId: tab.tabId,
    appId: tab.appId,
    instanceId: tab.instanceId,
    title: tab.title,
    isPinned: tab.isPinned,
  }
}

function savedTabToTab(savedTab: SavedTab): AppTab {
  return {
    tabId: savedTab.tabId,
    appId: savedTab.appId,
    instanceId: savedTab.instanceId,
    title: savedTab.title,
    isActive: false,
    isPinned: savedTab.isPinned,
  }
}
